import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  Plus,
  Trash2,
  Percent,
  DollarSign,
  Calendar,
  FileText,
  Package,
  Users,
  Building2,
  MapPin,
  ChevronDown,
  Check,
  Info,
  Sparkles,
  X,
  Copy,
} from 'lucide-react';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

// Small helpers
const uid = () => Math.random().toString(36).slice(2, 10);
const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const fmt = (n, cur) => new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 0 }).format(n || 0);

// Default HSN for Indian software services
const DEFAULT_HSN = '997331';

// Line items seeded from CORE calculator payload
const seedFromCorePayload = (payload) => {
  if (!payload) return [];
  const items = [];
  if (payload.tier_id && payload.tier_price != null) {
    items.push({
      id: uid(), type: 'tier', sku: payload.tier_id,
      description: `${payload.tier_name} \u2014 Perpetual License`,
      qty: 1, unit_price: payload.tier_price, hsn_sac: DEFAULT_HSN,
      recurring: 'one_time',
    });
    if (payload.amc_amount) {
      items.push({
        id: uid(), type: 'amc', sku: `${payload.tier_id}-amc`,
        description: `Annual Maintenance Contract (20%) \u2014 ${payload.tier_name}`,
        qty: 1, unit_price: payload.amc_amount, hsn_sac: DEFAULT_HSN,
        recurring: 'annual',
      });
    }
  }
  (payload.addons || []).forEach((a) => {
    items.push({
      id: uid(), type: 'addon', sku: a.id,
      description: a.on_request ? `${a.name} (On-Request)` : a.name,
      qty: a.qty, unit_price: a.unit_price, hsn_sac: DEFAULT_HSN,
      recurring: 'one_time',
    });
  });
  return items;
};

const seedFromPtiPayload = (payload) => {
  if (!payload) return [];
  const items = [];
  items.push({
    id: uid(), type: 'tier', sku: payload.tier_id,
    description: `${payload.tier_name} \u2014 Base Subscription`,
    qty: 12, unit_price: payload.tier_price || 0, hsn_sac: DEFAULT_HSN,
    recurring: 'monthly',
  });
  if (payload.extra_lines) {
    payload.extra_lines.forEach((l) => items.push({ ...l, id: uid(), hsn_sac: DEFAULT_HSN }));
  }
  return items;
};

const QuoteBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const payload = location.state || null;
  const product = payload?.product || 'core';

  // Catalog
  const [regions, setRegions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // Header
  const [regionId, setRegionId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [validUntil, setValidUntil] = useState(todayPlus(15));

  // Line items
  const [lineItems, setLineItems] = useState(() =>
    product === 'core' ? seedFromCorePayload(payload) : seedFromPtiPayload(payload)
  );

  // Custom fields
  const [customFields, setCustomFields] = useState([]);

  // Discounts
  const [discountPct, setDiscountPct] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Tax
  const [taxRate, setTaxRate] = useState(0);
  const [taxLabel, setTaxLabel] = useState('Tax');
  const [isIntraState, setIsIntraState] = useState(true);

  // T&Cs
  const [terms, setTerms] = useState('');

  // Notes / customer info snapshot
  const [notes, setNotes] = useState('');
  const [customerSnapshot, setCustomerSnapshot] = useState({
    name: '', contact_name: '', contact_email: '', billing_address: '', tax_id: '', country: '', state: '',
  });

  // Saving state
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // {id, quote_number, share_url}
  const [err, setErr] = useState('');

  // Load catalog
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, c] = await Promise.all([api.get('/regions'), api.get('/customers')]);
        if (cancelled) return;
        const sortedR = r.data.filter((x) => x.active).sort((a, b) => a.name.localeCompare(b.name));
        setRegions(sortedR);
        setCustomers(c.data.sort((a, b) => a.name.localeCompare(b.name)));

        // Default region: India if payload region_code = india, else first
        let initialRegion = sortedR.find((x) => x.code === 'IN')?.id || sortedR[0]?.id || '';
        if (payload?.region_code) {
          const matchByCode = sortedR.find((x) => x.code.toLowerCase() === payload.region_code.toLowerCase());
          if (matchByCode) initialRegion = matchByCode.id;
        }
        setRegionId(initialRegion);
      } catch (e) { /* ignore */ }
      setLoadingCatalog(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When region changes: apply region defaults (currency, tax, T&C template)
  useEffect(() => {
    if (!regionId) return;
    const r = regions.find((x) => x.id === regionId);
    if (r) {
      setCurrency(r.default_currency);
      setTaxRate(r.default_tax_rate || 0);
      setTaxLabel(r.tax_label || 'Tax');
      setTerms((prev) => prev || r.terms_template || '');
    }
    setCustomerId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionId, regions.length]);

  // When customer changes: apply price list overrides + prefill snapshot
  useEffect(() => {
    (async () => {
      if (!customerId) return;
      const c = customers.find((x) => x.id === customerId);
      if (c) {
        setCustomerSnapshot({
          name: c.name || '',
          contact_name: c.contact_name || '',
          contact_email: c.contact_email || '',
          billing_address: c.billing_address || '',
          tax_id: c.tax_id || '',
          country: c.country || '',
          state: c.state || '',
        });
        // Intra-state = customer state matches "Maharashtra" (Itanta HQ). Simplified.
        setIsIntraState((c.state || '').toLowerCase() === 'maharashtra');
      }
      // Resolve price list
      try {
        const pl = await api.get('/price-lists/resolve', {
          params: { product, region_id: regionId, customer_id: customerId },
        });
        if (pl.data) {
          const overrides = pl.data.tier_overrides || [];
          setCurrency(pl.data.currency);
          setLineItems((prev) => prev.map((li) => {
            const ov = overrides.find((o) => o.tier_id === li.sku);
            return ov ? { ...li, unit_price: ov.price } : li;
          }));
        }
      } catch (e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // Totals (real-time)
  const totals = useMemo(() => {
    const subtotal = lineItems.reduce((s, li) => s + (li.qty * li.unit_price), 0);
    const discountFromPct = subtotal * (discountPct || 0);
    const discountTotal = discountFromPct + (discountAmount || 0);
    const taxable = Math.max(subtotal - discountTotal, 0);
    const taxTotal = taxable * (taxRate || 0);
    const grand = taxable + taxTotal;
    return { subtotal, discountTotal, taxable, taxTotal, grand };
  }, [lineItems, discountPct, discountAmount, taxRate]);

  const addLineItem = () => setLineItems([...lineItems, {
    id: uid(), type: 'custom', sku: null, description: '', qty: 1, unit_price: 0,
    hsn_sac: DEFAULT_HSN, recurring: 'one_time',
  }]);
  const updateLine = (id, patch) => setLineItems(lineItems.map((li) => li.id === id ? { ...li, ...patch } : li));
  const removeLine = (id) => setLineItems(lineItems.filter((li) => li.id !== id));

  const addCustomField = () => setCustomFields([...customFields, { id: uid(), label: '', value: '' }]);
  const updateCF = (id, patch) => setCustomFields(customFields.map((f) => f.id === id ? { ...f, ...patch } : f));
  const removeCF = (id) => setCustomFields(customFields.filter((f) => f.id !== id));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        product,
        customer_id: customerId || null,
        region_id: regionId,
        currency,
        status: 'draft',
        version: {
          valid_until: validUntil,
          line_items: lineItems.map((li) => ({
            id: li.id, type: li.type, sku: li.sku, description: li.description,
            qty: parseFloat(li.qty) || 0, unit_price: parseFloat(li.unit_price) || 0,
            hsn_sac: li.hsn_sac, recurring: li.recurring,
            subtotal: (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0),
          })),
          custom_fields: customFields.filter((f) => f.label),
          discount_pct: discountPct || 0,
          discount_amount: discountAmount || 0,
          tax_rate: taxRate || 0,
          tax_label: taxLabel,
          is_intra_state: isIntraState,
          terms_and_conditions: terms,
          notes,
          customer_snapshot: customerSnapshot,
          company_snapshot: {
            name: 'Itanta Data Science Pvt Ltd',
            address: 'S.No. 284/2D, 1st Floor, Ideas To Impacts, Pallod Farms Lane - 3, Behind Vijay Sales, Baner, Pune, Maharashtra - 411045, India',
            gstin: '', pan: '',
          },
          subtotal: totals.subtotal,
          discount_total: totals.discountTotal,
          taxable_amount: totals.taxable,
          tax_total: totals.taxTotal,
          grand_total: totals.grand,
        },
      };
      const r = await api.post('/quotes', body);
      const shareUrl = `${window.location.origin}/quote/${r.data.id}`;
      setSaved({ id: r.data.id, quote_number: r.data.quote_number, share_url: shareUrl });
    } catch (ex) {
      setErr(errorMessage(ex, 'Failed to save quote'));
    }
    setSaving(false);
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px]">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="h-9 w-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors">
          <ArrowLeft className="w-4 h-4 text-white/70" />
        </button>
        <div>
          <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
            New quotation · {product.toUpperCase()}
          </div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Build a quotation</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6">
        {/* LEFT — form */}
        <div className="space-y-6">
          {/* Region + Customer */}
          <Section title="Region & customer" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField label="Region" value={regionId} onChange={setRegionId}
                options={[{ value: '', label: loadingCatalog ? 'Loading…' : 'Select region' }, ...regions.map((r) => ({ value: r.id, label: r.name }))]} required />
              <SelectField label="Customer" value={customerId} onChange={setCustomerId}
                options={[{ value: '', label: 'Select existing customer (optional)' }, ...customers.filter((c) => !regionId || c.region_id === regionId).map((c) => ({ value: c.id, label: c.name }))]} />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Company name" value={customerSnapshot.name} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, name: v })} required />
              <TextField label="Contact name" value={customerSnapshot.contact_name} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, contact_name: v })} />
              <TextField label="Contact email" type="email" value={customerSnapshot.contact_email} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, contact_email: v })} />
              <TextField label="Tax ID / GSTIN" value={customerSnapshot.tax_id} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, tax_id: v })} />
              <TextField label="State" value={customerSnapshot.state} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, state: v })} />
              <TextField label="Country" value={customerSnapshot.country} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, country: v })} />
              <TextareaField className="md:col-span-2" label="Billing address" value={customerSnapshot.billing_address} onChange={(v) => setCustomerSnapshot({ ...customerSnapshot, billing_address: v })} rows={2} />
            </div>
          </Section>

          {/* Line items */}
          <Section title="Line items" icon={Package} right={
            <button onClick={addLineItem} className="h-8 px-3 rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-[12px] flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add line
            </button>
          }>
            {lineItems.length === 0 ? (
              <div className="text-center py-10 text-white/40 text-sm">No items yet. Click "Add line" to start.</div>
            ) : (
              <div className="space-y-3">
                {lineItems.map((li, idx) => (
                  <LineItemRow key={li.id} li={li} idx={idx} currency={currency} onChange={(p) => updateLine(li.id, p)} onRemove={() => removeLine(li.id)} />
                ))}
              </div>
            )}
          </Section>

          {/* Custom fields */}
          <Section title="Custom fields" icon={FileText} right={
            <button onClick={addCustomField} className="h-8 px-3 rounded-lg bg-white/[0.04] hover:bg-white/[0.10] border border-white/10 text-white/80 font-semibold text-[12px] flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Add field
            </button>
          }>
            {customFields.length === 0 ? (
              <div className="text-[13px] text-white/40">
                Add arbitrary fields like "Project code", "PO number", "SLA reference"…
              </div>
            ) : (
              <div className="space-y-2">
                {customFields.map((f) => (
                  <div key={f.id} className="grid grid-cols-[1fr_2fr_auto] gap-2">
                    <input value={f.label} onChange={(e) => updateCF(f.id, { label: e.target.value })} placeholder="Field label"
                      className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13.5px] focus:outline-none focus:border-cyan-400/50" />
                    <input value={f.value} onChange={(e) => updateCF(f.id, { value: e.target.value })} placeholder="Value"
                      className="h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13.5px] focus:outline-none focus:border-cyan-400/50" />
                    <button onClick={() => removeCF(f.id)} className="h-9 w-9 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 inline-flex items-center justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Discounts + Tax */}
          <Section title="Discount & tax" icon={Percent}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumberField label="Discount %" value={discountPct * 100} onChange={(v) => setDiscountPct((parseFloat(v) || 0) / 100)} suffix="%" />
              <NumberField label="Discount amount (fixed)" value={discountAmount} onChange={(v) => setDiscountAmount(parseFloat(v) || 0)} prefix={currency} />
              <NumberField label={`Tax rate — ${taxLabel}`} value={taxRate * 100} onChange={(v) => setTaxRate((parseFloat(v) || 0) / 100)} suffix="%" />
              <TextField label="Tax label" value={taxLabel} onChange={setTaxLabel} placeholder="GST / VAT / Sales tax" />
              {taxLabel.toUpperCase().includes('GST') && (
                <label className="flex items-center gap-2 col-span-2 mt-1">
                  <input type="checkbox" checked={isIntraState} onChange={(e) => setIsIntraState(e.target.checked)} className="h-4 w-4" />
                  <span className="text-[13px] text-white/70">
                    Intra-state supply (Maharashtra) — split as CGST + SGST. Uncheck for inter-state (IGST).
                  </span>
                </label>
              )}
            </div>
          </Section>

          {/* T&Cs */}
          <Section title="Terms & conditions" icon={FileText} right={
            <span className="text-[11px] text-white/45">
              Region template loaded — edit freely for this quote
            </span>
          }>
            <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={12}
              className="w-full px-4 py-3 rounded-lg bg-white/[0.03] border border-white/10 text-white font-mono text-[12.5px] leading-relaxed focus:outline-none focus:border-cyan-400/50 resize-y" />
          </Section>

          {/* Notes */}
          <Section title="Internal notes" icon={Info}>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full px-4 py-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13.5px] focus:outline-none focus:border-cyan-400/50 resize-y"
              placeholder="Notes visible to admin only, not on the customer-facing quote…" />
          </Section>
        </div>

        {/* RIGHT — sticky summary */}
        <aside className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-6 lg:sticky lg:top-6 lg:self-start h-fit">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-white/50">Live total</div>
              <div className="text-[13px] text-white/60 mt-1">Product: <span className="text-white/85 font-semibold">{product.toUpperCase()}</span></div>
            </div>
            <Sparkles className="w-5 h-5 text-emerald-300" />
          </div>

          <div className="mt-5">
            <label className="block">
              <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">Valid until</span>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                className="mt-1.5 w-full h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13px] focus:outline-none focus:border-cyan-400/50" />
            </label>
          </div>

          <div className="mt-6 space-y-2 text-[13.5px]">
            <Row label="Subtotal" value={fmt(totals.subtotal, currency)} />
            {totals.discountTotal > 0 && (
              <Row label={`Discount${discountPct > 0 ? ` (${(discountPct * 100).toFixed(0)}%)` : ''}`} value={`− ${fmt(totals.discountTotal, currency)}`} highlight="red" />
            )}
            <div className="pt-2 border-t border-white/5">
              <Row label="Taxable amount" value={fmt(totals.taxable, currency)} />
            </div>
            {taxRate > 0 && taxLabel.toUpperCase().includes('GST') && isIntraState ? (
              <>
                <Row label={`CGST (${(taxRate * 50).toFixed(1)}%)`} value={fmt(totals.taxTotal / 2, currency)} sub />
                <Row label={`SGST (${(taxRate * 50).toFixed(1)}%)`} value={fmt(totals.taxTotal / 2, currency)} sub />
              </>
            ) : taxRate > 0 && taxLabel.toUpperCase().includes('GST') ? (
              <Row label={`IGST (${(taxRate * 100).toFixed(1)}%)`} value={fmt(totals.taxTotal, currency)} sub />
            ) : taxRate > 0 ? (
              <Row label={`${taxLabel} (${(taxRate * 100).toFixed(1)}%)`} value={fmt(totals.taxTotal, currency)} sub />
            ) : null}
          </div>

          <div className="mt-5 rounded-xl border border-emerald-400/25 bg-emerald-400/[0.05] p-4">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-semibold tracking-[0.18em] uppercase text-emerald-300">Grand total</span>
              <span className="text-2xl font-bold text-white tabular-nums">{fmt(totals.grand, currency)}</span>
            </div>
          </div>

          {err && <div className="mt-4 text-[13px] text-red-300">{err}</div>}

          {saved ? (
            <div className="mt-5 space-y-3">
              <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.06] p-4">
                <div className="text-[11px] tracking-[0.16em] uppercase font-semibold text-cyan-300">Quote saved</div>
                <div className="mt-1 text-white font-semibold">{saved.quote_number}</div>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2">
                  <input readOnly value={saved.share_url} className="flex-1 bg-transparent text-[12px] text-white/80 outline-none" />
                  <button onClick={() => navigator.clipboard.writeText(saved.share_url)}
                    className="text-white/60 hover:text-white transition-colors">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <Link to={`/admin/quotes/${saved.id}`} className="h-11 w-full rounded-lg bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm flex items-center justify-center">
                Open quote →
              </Link>
              <Link to="/admin/quotes" className="text-center block text-[12.5px] text-white/60 hover:text-white transition-colors">
                Back to all quotes
              </Link>
            </div>
          ) : (
            <button onClick={save} disabled={saving || !regionId || !customerSnapshot.name}
              className="mt-5 h-11 w-full rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save quotation'}
            </button>
          )}

          <div className="mt-4 text-[11px] text-white/40 leading-relaxed">
            Each save creates a new version under the same quote number. Versions are tracked in the quote history.
          </div>
        </aside>
      </div>
    </div>
  );
};

// --- Subcomponents ---
const Section = ({ title, icon: Icon, right, children, className = '' }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-white">
        <span className="h-7 w-7 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon className="w-3.5 h-3.5 text-cyan-300" />
        </span>
        <span className="font-semibold">{title}</span>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const LineItemRow = ({ li, idx, currency, onChange, onRemove }) => {
  const subtotal = (parseFloat(li.qty) || 0) * (parseFloat(li.unit_price) || 0);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="grid grid-cols-12 gap-2 items-center">
        <div className="col-span-12 md:col-span-5">
          <input value={li.description} onChange={(e) => onChange({ description: e.target.value })} placeholder={`Line ${idx + 1} description`}
            className="w-full h-9 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[13.5px] focus:outline-none focus:border-cyan-400/50" />
        </div>
        <div className="col-span-4 md:col-span-2">
          <select value={li.recurring} onChange={(e) => onChange({ recurring: e.target.value })}
            className="w-full h-9 px-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-[12.5px] focus:outline-none focus:border-cyan-400/50">
            <option value="one_time">One-time</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="col-span-4 md:col-span-1">
          <input type="number" min="0" step="1" value={li.qty} onChange={(e) => onChange({ qty: e.target.value })} placeholder="Qty"
            className="w-full h-9 px-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-right tabular-nums text-[13px] focus:outline-none focus:border-cyan-400/50" />
        </div>
        <div className="col-span-4 md:col-span-2">
          <input type="number" min="0" step="0.01" value={li.unit_price} onChange={(e) => onChange({ unit_price: e.target.value })} placeholder="Unit price"
            className="w-full h-9 px-2 rounded-lg bg-white/[0.03] border border-white/10 text-white text-right tabular-nums text-[13px] focus:outline-none focus:border-cyan-400/50" />
        </div>
        <div className="col-span-9 md:col-span-1 text-right text-white font-semibold tabular-nums text-[13.5px]">
          {fmt(subtotal, currency)}
        </div>
        <div className="col-span-3 md:col-span-1 text-right">
          <button onClick={onRemove} className="h-8 w-8 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-400/10 inline-flex items-center justify-center">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-white/45">
        <span>HSN/SAC:</span>
        <input value={li.hsn_sac || ''} onChange={(e) => onChange({ hsn_sac: e.target.value })} placeholder="997331"
          className="w-24 h-6 px-2 rounded bg-white/[0.02] border border-white/10 text-white/80 focus:outline-none focus:border-cyan-400/50 font-mono" />
        <span className="mx-2">·</span>
        <span>Type:</span>
        <select value={li.type} onChange={(e) => onChange({ type: e.target.value })}
          className="h-6 px-1.5 rounded bg-white/[0.02] border border-white/10 text-white/80 focus:outline-none focus:border-cyan-400/50">
          <option value="tier">Tier</option>
          <option value="addon">Add-on</option>
          <option value="amc">AMC</option>
          <option value="service">Service</option>
          <option value="custom">Custom</option>
        </select>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, type = 'text', required, placeholder, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">{label}</span>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
      className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]" />
  </label>
);
const NumberField = ({ label, value, onChange, suffix, prefix }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">{label}</span>
    <div className="mt-1.5 relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45 text-[12px] font-mono">{prefix}</span>}
      <input type="number" min="0" step="0.01" value={value ?? 0} onChange={(e) => onChange(e.target.value)}
        className={`w-full h-10 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] tabular-nums ${prefix ? 'pl-11' : 'pl-3'} ${suffix ? 'pr-10' : 'pr-3'}`} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 text-[12px]">{suffix}</span>}
    </div>
  </label>
);
const TextareaField = ({ label, value, onChange, rows = 3, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">{label}</span>
    <textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px] resize-y" />
  </label>
);
const SelectField = ({ label, value, onChange, options, required }) => (
  <label className="block">
    <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/55">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} required={required}
      className="mt-1.5 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50 text-[14px]">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);
const Row = ({ label, value, sub, highlight }) => (
  <div className={`flex items-center justify-between ${sub ? 'text-[12.5px] text-white/60 pl-2' : 'text-white/80'}`}>
    <span>{label}</span>
    <span className={`tabular-nums ${highlight === 'red' ? 'text-red-300' : 'text-white'}`}>{value}</span>
  </div>
);

export default QuoteBuilder;
